import React from 'react';
import PropTypes from 'prop-types';
import _, { result } from 'underscore';
import { RecommendationModal, AsyncBulkAarConfirmForm } from '@bingads-webui-campaign-react/recommendation-modal';
import { UPLOAD_COMFIRM, SUPPORT_RECOMMENDATION_TYPE_TITLE, LIMIT_UPLOAD_NUMBER } from './const';

export class BulkAARUpload extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
    newI18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
    dataService: PropTypes.shape({
      getBulkAarUploadBlobUrl: PropTypes.func.isRequired,
      setBulkAarUploadBlob: PropTypes.func.isRequired,
      submitBulkAarOptInStatusTask: PropTypes.func.isRequired,
      pollBulkAarOptInStatusTask: PropTypes.func.isRequired,
    }).isRequired,
    serverContext: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  constructor(props) {
    super(props);
    this.fileInput = React.createRef();
    this.state = {
      fileName: null,
      fileContent: null,
      errorMessage: '',
      isBulkAarUploadModalOpen: false,
      uploadContent: props.newI18n.getString(UPLOAD_COMFIRM, { number: 0 }),
      loading: false,
      stage: 0,
    };
  }

  onClick = () => {
    this.fileInput.current.value = '';
    this.fileInput.current.click();
  };

  filter = (element, seenRow, seenAccount) => {
    const rowChain = _.chain(element.split(/,/)).map(column => column.trim()).value();
    const accountIdRegex = /^[0-9]*$/;
    const optInRegex = /\byes\b|\bno\b/;

    if (rowChain[0] && rowChain[1] && rowChain[2]) {
      if (accountIdRegex.test(rowChain[0]) &&
        _.contains(SUPPORT_RECOMMENDATION_TYPE_TITLE, rowChain[1]) &&
        optInRegex.test(rowChain[2].toLowerCase())) {
        seenRow.add(`${rowChain[0]}, ${rowChain[1]}`);
        seenAccount.add(rowChain[0]);
        return true;
      }
    }

    return false;
  };

  parseCsvFromFile = (stream) => {
    if (!stream) {
      return 0;
    }

    const emptyRegex = /^,,$/; // stream should filter out lines containing only commas
    const rowCount = _.chain(stream.split(/\r\n|\n/)).filter(row => !emptyRegex.test(row)).filter(row => row.trim()).value().length - 1; // minus 1 for the header row

    const seenRow = new Set();
    const seenAccount = new Set();
    const optIn = _.chain(stream.split(/\r\n|\n/))
      .filter(row => this.filter(row, seenRow, seenAccount))
      .value();

    let output = 'Account Id, Recommendation Type, Opt-In\n';
    _.each(optIn, (optInElement) => {
      const rowChain = _.chain(optInElement.split(/,/)).map(column => column.trim()).value();
      output += `${rowChain[0]}, ${rowChain[1]}, ${rowChain[2]}\n`;
    });

    return {
      content: output,
      count: optIn.length,
      rowCount,
      uniqueCount: seenRow.size,
      accountCount: seenAccount.size,
    };
  };

  handleFile = (e) => {
    const { files } = e.target;
    if (files.length > 0 && files[0]) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        this.saveCurrent(file);

        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = () => {
          const validContent = this.parseCsvFromFile(reader.result);

          if (validContent.count === validContent.rowCount &&
            validContent.count === validContent.uniqueCount &&
            validContent.accountCount <= LIMIT_UPLOAD_NUMBER) {
            this.setState({
              isBulkAarUploadModalOpen: true,
              uploadContent: this.props.newI18n.getString(UPLOAD_COMFIRM, { number: validContent.count }),
              fileContent: validContent.content,
            });
          } else if (validContent.accountCount > LIMIT_UPLOAD_NUMBER) {
            this.saveCurrent(file, this.props.newI18n.getString(_TL_('There are more than {{limit}} account id in the CSV file, please remove some and try again.'), { limit: LIMIT_UPLOAD_NUMBER }));
          } else if (validContent.count !== validContent.uniqueCount) {
            this.saveCurrent(file, this.props.newI18n.getString(_TL_('There are duplicated records in the CSV file, please fix them and then try again.')));
          } else {
            this.saveCurrent(file, this.props.newI18n.getString(_TL_('Some of rows in the CSV file have invalid account id, recommendation type name or opt-in status, please fix them and then try again.')));
          }
        };
      } else {
        this.saveCurrent(null, this.props.newI18n.getString(_TL_('The format of the file is not supported, please upload CSV file.')));
      }
    }
  };

  saveCurrent = (file, errorMessage = '') => {
    this.setState({
      fileName: file && `${result(this.props.serverContext, ['CurrentUser', 'Id'])}_${Date.now()}_${file.name}`,
      errorMessage,
    });
  };

  confirmBulkAarUploadModal = () => {
    const { fileName, fileContent } = this.state;
    this.setState({ loading: true, stage: 0 });
    this.props.dataService.getBulkAarUploadBlobUrl({ fileName })
      .then(({ data }) => {
        if (data) {
          return this.props.dataService.setBulkAarUploadBlob(data.value, fileContent);
        }
        return null;
      })
      .then((response) => {
        if (response.ok) {
          this.setState({ stage: 1 });
          return this.props.dataService.submitBulkAarOptInStatusTask({ fileName });
        }
        return null;
      })
      .then(({ res }) => {
        if (res.value) {
          this.setState({ stage: 2 });
          return this.props.dataService.pollBulkAarOptInStatusTask({ reqId: res.value });
        }
        return null;
      })
      .then(() => {})
      .finally(() => { this.closeBulkAarUploadModalOpen(); });
  };

  closeBulkAarUploadModalOpen = () => {
    this.setState({
      fileName: '',
      isBulkAarUploadModalOpen: false,
      uploadContent: this.props.newI18n.getString(UPLOAD_COMFIRM, { number: 0 }),
      loading: false,
      errorMessage: '',
    });
  };

  render() {
    const {
      classes, i18n, newI18n,
    } = this.props;
    const {
      errorMessage, uploadContent,
    } = this.state;

    return (
      <div id="bulk-aar-upload-container" className={classes.uploadContatiner}>
        <button id="btn-select-file" className="btn btn-default" onClick={this.onClick}>
          {newI18n.getString(_TL_('Choose file...'))}
        </button>
        <input
          type="file"
          id="bulk-aar-upload"
          className={classes.uploadInput}
          onChange={this.handleFile}
          accept=".csv"
          ref={this.fileInput}
        />
        {
          !_.isEmpty(errorMessage) &&
          <div id="file-error" className={classes.fileError}>
            {errorMessage}
          </div>
        }
        <RecommendationModal
          isOpen={this.state.isBulkAarUploadModalOpen}
          onCancel={this.closeBulkAarUploadModalOpen}
          closeOnOverlayClick={false}
        >
          <AsyncBulkAarConfirmForm
            title={this.state.loading ? newI18n.getString(_TL_('Update')) : newI18n.getString(_TL_('Upload'))}
            content={uploadContent}
            confirmString={newI18n.getString(_TL_('Apply changes'))}
            cancelString={i18n.getString('Cancel')}
            onConfirm={this.confirmBulkAarUploadModal}
            onCancel={this.closeBulkAarUploadModalOpen}
            loading={this.state.loading}
            stage={this.state.stage}
            i18n={i18n}
            newI18n={newI18n}
          />
        </RecommendationModal>
      </div>
    );
  }
}
